// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import i18n from '../../../../../../locale/i18n';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Plugins
import {
  convertLength,
  convertPressure,
  convertTemperature,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
  formatValueWithUnit,
  roundNumber,
} from '../../../../../../plugins/general';
import {
  booleanStatusToTagType,
  booleanToOnOffText,
  isDakotaModel,
} from '../../../../../../plugins/crusher';

// Store
import { getMeasurementUnit } from '../../../../../../store/measurement.store';

// Types
import { CardSubItem } from '../../../../../../layout/elements/CardList/CardList.types';
import {
  MeasurementType,
  LengthType,
  PressureType,
  TemperatureType,
} from '../../../../../../types/global';

// Components
import {
  MaintenanceCardList,
  MaintenanceCardItem,
  MaintenanceCardSection,
} from '../../../../../../components/MaintenanceCardList';
import { NavigationTabs } from '../../../../../../layout/elements/NavigationTabs';
import { Tag } from '../../../../../../layout/elements/Tag';

// Modules
import { Automation } from './Modules/Automation';
import { Crusher } from './Modules/Crusher';
import { Hydraulic } from './Modules/Hydraulic';
import { Lubrication } from './Modules/Lubrication';
import { Vibration } from './Modules/Vibration';

// Component
import {
  SetpointContext,
  SetpointItemTab,
  SetpointProps,
  SetpointType,
} from './Setpoint.types';
import {
  Container,
  FixedHeader,
  ScrollableContent,
  Separator,
} from './Setpoint.style';

const mapSensorToSubItem = (
  items: { id?: string; sensor: string; setpoint?: string | JSX.Element }[],
): CardSubItem[] => items
  .filter((item) => item.setpoint !== undefined)
  .map((item) => ({
    ...(item.id && { id: item.id }),
    label: item.sensor,
    value: item.setpoint as string | JSX.Element,
  }));

const getCrusherCardItem = (context: SetpointContext): MaintenanceCardItem => {
  const { unitData, temperatureUnit } = context;

  const sections: MaintenanceCardSection[] = [];

  sections.push({
    key: 'crusher-motor-specs',
    title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.crusherMotorSpecsTitle')}`,
    subItems: mapSensorToSubItem([
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.fullLoadDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spCrusherMotorFullLoadAmps, 'A'),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.serviceFactorDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spCrusherMotorServiceFactor, 'SF', 2),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.runningAmpsDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spCrusherMotorRunningAmps, 'A'),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.crushingAmpsDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spCrusherMotorCrushingAmps, 'A'),
      },
    ]),
  });

  sections.push({
    key: 'warning-horn-seconds',
    title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.warningHornSecondsTitle')}`,
    subItems: mapSensorToSubItem([
      ...(unitData?.eccentricBushingTempEnable ? [{
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.dischargeStartDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spDischargeWarningHornTime, 's'),
      }] : []),
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.crusherMotorStopDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spCrusherMotorWarningHornTime, 's'),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.feederStartDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spFeederWarningHornTime, 's'),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.crusherAdjustmentDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spCrusherAdjustmentWarningHornTime, 's'),
      },
    ]),
  });

  sections.push({
    key: 'over-current-protection',
    title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.overCurrentProtectionTitle')}`,
    subItems: mapSensorToSubItem([
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.timeAboveFlaSF1.15Description')}`,
        setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaSf115FeedStop, 's'),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.timeAboveFlaDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaFeedStop, 's'),
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.timeAboveFlaSFDescription')}`,
        setpoint: formatValueWithUnit(unitData?.spTimeAboveFlaSfFeedStop, 's'),
      },
    ]),
  });

  if (unitData?.cavityLevelSensorEnable) {
    sections.push({
      key: 'cavity-level',
      title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.cavityLevelTitle')}`,
      subItems: mapSensorToSubItem([
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.maxLevelDescription')}`,
          setpoint: formatValueWithUnit(unitData?.spCavityLevelMaxLevel, '%'),
        },
      ]),
    });
  }

  if (unitData?.beltSlipMonitorEnable) {
    sections.push({
      key: 'belt-slip',
      title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.beltSlipTitle')}`,
      subItems: mapSensorToSubItem([
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.beltSlipDescription')}`,
          setpoint: formatValueWithUnit(unitData?.spBeltSlipAlarmRpm, 'RPM'),
        },
      ]),
    });
  }

  if (unitData?.counterShaftBushingTempEnable) {
    sections.push({
      key: 'counter-shaft-temp',
      title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.counterShaftTempTitle')}`,
      subItems: mapSensorToSubItem([
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.overTempWarningDescription')}`,
          setpoint: convertTemperature(
            unitData?.spCounterShaftTempWarning,
            temperatureUnit,
            0,
            true,
          ).formattedValue,
        },
      ]),
    });
  }

  if (unitData?.eccentricBushingTempEnable) {
    sections.push({
      key: 'eccentric-bushing-temp',
      title: `${i18n.t('view.crusher.maintenance.setPoint.crusher.eccentricBushingTempTitle')}`,
      subItems: mapSensorToSubItem([
        ...(unitData?.eccentricBushingTempEnable ? [{
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.tempHighDescription')}`,
          setpoint: convertTemperature(
            unitData?.spEccentricBushingTempHigh,
            temperatureUnit,
            0,
            true,
          ).formattedValue,
        }] : []),
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.tempHighFeedDescription')}`,
          setpoint: convertTemperature(
            unitData?.spEccentricBushingTempHighStopFeed,
            temperatureUnit,
            0,
            true,
          ).formattedValue,
        },
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.crusher.temperatureHighCrusherDescription')}`,
          setpoint: convertTemperature(
            unitData?.spEccentricBushingTempHighStopCrusher,
            temperatureUnit,
            0,
            true,
          ).formattedValue,
        },
      ]),
    });
  }

  return {
    key: 'crusher',
    title: `${i18n.t('common.equipmentCategory.crusher.singular')}`,
    subItems: [],
    sections,
  };
};

const getLubricationCardItem = (context: SetpointContext): MaintenanceCardItem => {
  const {
    unitData,
    temperatureUnit,
    pressureUnit,
    isDakota,
  } = context;

  const sections: MaintenanceCardSection[] = [];

  sections.push({
    key: 'lube-return-temp',
    title: i18n.t('view.crusher.maintenance.setPoint.lubrication.returnTempTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.lowAlarmDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeReturnTempLowAlarm,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.lowWarningDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeReturnTempLowWarning,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.crusherStartDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeReturnTempCrusherStartPermissive,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.highAlarmDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeReturnTempHighWarn,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.stopFeedDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeReturnTempStopFeed,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.crusherStopDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeReturnTempCrusherStop,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  sections.push({
    key: 'lube-tank-temp',
    title: i18n.t('view.crusher.maintenance.setPoint.lubrication.tankTempTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.pumpRunDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeTankTempLowAlarm,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.pumpStartDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeTankTempLowWarning,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.highWarningDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeTankTempHighWarning,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.highAlarmDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeTankTempHighAlarm,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  if (!isDakota) {
    sections.push({
      key: 'lube-pressure',
      title: i18n.t('view.crusher.maintenance.setPoint.lubrication.lubePressureTitle'),
      subItems: mapSensorToSubItem([
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.normalPressureDescription')}`,
          setpoint: convertPressure(
            unitData?.spLubePressureNormal,
            pressureUnit,
            0,
            true,
          ).formattedValue,
        },
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.feedStopDescription')}`,
          setpoint: convertPressure(
            unitData?.spLubePressureLowWarning,
            pressureUnit,
            0,
            true,
          ).formattedValue,
        },
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.crusherStopLowAlarmDescription')}`,
          setpoint: convertPressure(
            unitData?.spLubePressureLowAlarm,
            pressureUnit,
            0,
            true,
          ).formattedValue,
        },
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.lubeStopDescription')}`,
          setpoint: convertPressure(
            unitData?.spLubePressureStopAlarm,
            pressureUnit,
            0,
            true,
          ).formattedValue,
        },
      ]),
    });
  }

  if (isDakota && unitData?.lubeFlowSwitchEnable) {
    sections.push({
      key: 'lube-flow',
      title: i18n.t('view.crusher.maintenance.setPoint.lubrication.lubeFlowTitle'),
      subItems: mapSensorToSubItem([
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.lubeFlowMinDescription')}`,
          setpoint: formatValueWithUnit(unitData?.spLubeFlowScaleMin, 'GPM'),
        },
        {
          sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.lubeFlowMaxDescription')}`,
          setpoint: formatValueWithUnit(unitData?.spLubeFlowScaleMax, 'GPM'),
        },
      ]),
    });
  }

  sections.push({
    key: 'lube-cooler',
    title: i18n.t('view.crusher.maintenance.setPoint.lubrication.lubeCoolerTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.coolerStartDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeCoolerStartTemp,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.coolerStopDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeCoolerStopTemp,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  sections.push({
    key: 'lube-heater',
    title: i18n.t('view.crusher.maintenance.setPoint.lubrication.lubeHeaterTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.startTempDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeHeaterStartTemp,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: `${i18n.t('view.crusher.maintenance.setPoint.lubrication.stopTempDescription')}`,
        setpoint: convertTemperature(
          unitData?.spLubeHeaterStopTemp,
          temperatureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  if (unitData?.lubeSupplyTempEnable) {
    sections.push({
      key: 'supply-return-temp-diff',
      title: i18n.t('view.crusher.maintenance.setPoint.lubrication.supplyReturnTempTitle'),
      subItems: mapSensorToSubItem([
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.lubrication.differentialAlarmDescription'),
          setpoint: convertTemperature(
            unitData?.spLubeSupplyReturnTempDiffAlarm,
            temperatureUnit,
            0,
            true,
          ).formattedValue,
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.lubrication.delayAfterCoolerDescription'),
          setpoint: formatValueWithUnit(unitData?.spLubeSupplyReturnTempDiffDelay, 's'),
        },
      ]),
    });

    sections.push({
      key: 'supply-temp',
      title: i18n.t('view.crusher.maintenance.setPoint.lubrication.supplyTempTitle'),
      subItems: mapSensorToSubItem([
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.lubrication.lowWarningDescription'),
          setpoint: convertTemperature(
            unitData?.spLubeSupplyTempLowWarn,
            temperatureUnit,
            0,
            true,
          ).formattedValue,
        },
      ]),
    });
  }

  return {
    key: 'lubrication',
    title: `${i18n.t('common.lubrication')}`,
    subItems: [],
    sections,
  };
};

const getHydraulicCardItem = (context: SetpointContext): MaintenanceCardItem => {
  const {
    unitData,
    edgeStatus,
    pressureUnit,
  } = context;

  const sections: MaintenanceCardSection[] = [];

  sections.push({
    key: 'tramp-pressure',
    title: i18n.t('view.crusher.maintenance.setPoint.hydraulic.trampPressureTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.startPressurizeDescription'),
        setpoint: convertPressure(
          unitData?.spTrampPressureStartPressurize,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.stopPressurizeDescription'),
        setpoint: convertPressure(
          unitData?.spTrampPressureStopPressurize,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.lowAlarmDescription'),
        setpoint: convertPressure(
          unitData?.spTrampPressureLowAlarm,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  sections.push({
    key: 'clamp-pressure',
    title: i18n.t('view.crusher.maintenance.setPoint.hydraulic.clampPressureTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.startPressurizeDescription'),
        setpoint: convertPressure(
          unitData?.spClampPressureStartPressurize,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.stopPressurizeDescription'),
        setpoint: convertPressure(
          unitData?.spClampPressureStopPressurize,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.lowAlarmDescription'),
        setpoint: convertPressure(
          unitData?.spClampPressureLowAlarm,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  sections.push({
    key: 'bowl-adjustment',
    title: i18n.t('view.crusher.maintenance.setPoint.hydraulic.bowlAdjustmentTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.maxClampPressureDescription'),
        setpoint: convertPressure(
          unitData?.spBowlAdjustmentMaxClampPressure,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.bowlAdjustmentDescription'),
        setpoint: (
          <Tag
            text={booleanToOnOffText(unitData?.spBowlAdjustmentUnderLoadBool)}
            type={booleanStatusToTagType(
              unitData?.spBowlAdjustmentUnderLoadBool,
              edgeStatus ? edgeStatus.edgeStatus : null,
              unitData?.timestamp,
            )}
          />
        ),
      },
    ]),
  });

  sections.push({
    key: 'system-pressure',
    title: i18n.t('view.crusher.maintenance.setPoint.hydraulic.systemPressureTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.hydraulic.systemPressureDescription'),
        setpoint: convertPressure(
          unitData?.spSystemPressureHighAlarm,
          pressureUnit,
          0,
          true,
        ).formattedValue,
      },
    ]),
  });

  return {
    key: 'hydraulic',
    title: `${i18n.t('common.hydraulic')}`,
    subItems: [],
    sections,
  };
};

const getVibrationCardItem = (
  context: SetpointContext,
): MaintenanceCardItem | null => {
  const { unitData } = context;

  const subItems: CardSubItem[] = [];

  if (unitData?.vibrationSensor1Enable) {
    subItems.push({
      label: `${i18n.t('view.crusher.maintenance.setPoint.vibration.vibration1Description')}`,
      value: formatValueWithUnit(unitData?.spVibration1Warning, '%'),
    });
  }
  if (unitData?.vibrationSensor2Enable) {
    subItems.push({
      label: `${i18n.t('view.crusher.maintenance.setPoint.vibration.vibration2Description')}`,
      value: formatValueWithUnit(unitData?.spVibration2Warning, '%'),
    });
  }
  if (unitData?.vibrationSensor3Enable) {
    subItems.push({
      label: `${i18n.t('view.crusher.maintenance.setPoint.vibration.vibration3Description')}`,
      value: formatValueWithUnit(unitData?.spVibration3Warning, '%'),
    });
  }
  if (unitData?.vibrationSensor4Enable) {
    subItems.push({
      label: `${i18n.t('view.crusher.maintenance.setPoint.vibration.vibration4Description')}`,
      value: formatValueWithUnit(unitData?.spVibration4Warning, '%'),
    });
  }

  if (subItems.length === 0) {
    return null;
  }

  return {
    key: 'vibration',
    title: `${i18n.t('common.vibration')}`,
    subItems: [],
    sections: [{
      key: 'vibration',
      title: i18n.t('view.crusher.maintenance.setPoint.vibration.title'),
      subItems,
    }],
  };
};

const getAutomationCardItem = (context: SetpointContext): MaintenanceCardItem => {
  const {
    unitData,
    edgeStatus,
    lengthUnit,
  } = context;

  const sections: MaintenanceCardSection[] = [];

  sections.push({
    key: 'auto-bowl-adjustment',
    title: i18n.t('view.crusher.maintenance.setPoint.automation.autoBowlTitle'),
    subItems: mapSensorToSubItem([
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.highVibrationOpenDescription'),
        setpoint: (
          <Tag
            text={booleanToOnOffText(unitData?.spAutoBowlAdjustVibrationEnable)}
            type={booleanStatusToTagType(
              unitData?.spAutoBowlAdjustVibrationEnable,
              edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
              unitData?.timestamp,
            )}
          />
        ),
      },
      {
        id: 'vib-css-adjust-open',
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cssAdjustOpenDescription'),
        setpoint: unitData?.spVibAlarmTeethToOpen ? `${formatNumber(roundNumber(
          unitData?.spVibAlarmTeethToOpen,
        ))} ${i18n.t('common.teeth')}` : DASHBOARD_NULLISH_TEXT,
      },
      {
        id: 'vib-count-feed-stop',
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.countForFeedDescription'),
        setpoint: unitData?.spVibCountFeedStoppage ? `${formatNumber(roundNumber(
          unitData?.spVibCountFeedStoppage,
        ))} ${i18n.t('common.events')}` : DASHBOARD_NULLISH_TEXT,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.vibTimeWindowDescription'),
        setpoint: formatValueWithUnit(unitData?.spVibShiftTimeHours, 'Hrs'),
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.highAmpsOpenDescription'),
        setpoint: (
          <Tag
            text={booleanToOnOffText(unitData?.spAmpAdjustHighAmpsEnable)}
            type={booleanStatusToTagType(
              unitData?.spAmpAdjustHighAmpsEnable,
              edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
              unitData?.timestamp,
            )}
          />
        ),
      },
      {
        id: 'amp-css-adjust-open',
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cssAdjustOpenDescription'),
        setpoint: unitData?.spAmpOpenAlarmTeeth ? `${formatNumber(roundNumber(
          unitData?.spAmpOpenAlarmTeeth,
        ))} ${i18n.t('common.teeth')}` : DASHBOARD_NULLISH_TEXT,
      },
      {
        id: 'amp-count-feed-stop',
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.countForFeedDescription'),
        setpoint: unitData?.spAmpCountFeedStoppage ? `${formatNumber(roundNumber(
          unitData?.spAmpCountFeedStoppage,
        ))} ${i18n.t('common.events')}` : DASHBOARD_NULLISH_TEXT,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.ampTimeWindowDescription'),
        setpoint: formatValueWithUnit(unitData?.spAmpShiftTimeHours, 'Hrs'),
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.lowAmpsClose'),
        setpoint: (
          <Tag
            text={booleanToOnOffText(unitData?.spAutoBowlAdjustLowAmpsEnable)}
            type={booleanStatusToTagType(
              unitData?.spAutoBowlAdjustLowAmpsEnable,
              edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
              unitData?.timestamp,
            )}
          />
        ),
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cssCloseAmountDescription'),
        setpoint: unitData?.spAmpCloseAlarmTeeth ? `${formatNumber(roundNumber(
          unitData?.spAmpCloseAlarmTeeth,
        ))} ${i18n.t('common.teeth')}` : DASHBOARD_NULLISH_TEXT,
      },
      {
        sensor: i18n.t('view.crusher.maintenance.setPoint.automation.minAdjustCloseCssDescription'),
        setpoint: convertLength(unitData?.spAutoCloseMinCss, lengthUnit, 2, true).formattedValue,
      },
    ]),
  });

  if (unitData?.cavityLevelSensorEnable) {
    sections.push({
      key: 'auto-feeder',
      title: i18n.t('view.crusher.maintenance.setPoint.automation.autoFeederTitle'),
      subItems: mapSensorToSubItem([
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.HybridLevelDescription'),
          setpoint: (
            <Tag
              text={booleanToOnOffText(
                _.isNil(unitData?.spAutoModeSelection)
                  ? undefined
                  : unitData?.spAutoModeSelection === 1,
              )}
              type={booleanStatusToTagType(
                unitData?.spAutoModeSelection === 1,
                edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
                unitData?.timestamp,
              )}
            />
          ),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.feederMaximumSpeedDescription'),
          setpoint: formatValueWithUnit(unitData?.spMaxFeederSpeed, '%'),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cavityLevelKPDescription'),
          setpoint: formatValueWithUnit(unitData?.spPidAutoLevelFeederSpeedKp, ''),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cavityLevelKiDescription'),
          setpoint: formatValueWithUnit(unitData?.spPidAutoLevelFeederSpeedKi, ''),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cavityLevelKdDescription'),
          setpoint: formatValueWithUnit(unitData?.spPidAutoLevelFeederSpeedKd, ''),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.crusherCurrentDescription'),
          setpoint: formatValueWithUnit(unitData?.spCrusherMotorAutoAmps, 'A'),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.crusherCurrentLowDescription'),
          setpoint: formatValueWithUnit(unitData?.spCrusherMotorAutoAmpsLowLimit, 'A'),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.crusherCurrentHighDescription'),
          setpoint: formatValueWithUnit(unitData?.spCrusherMotorAutoAmpsHighLimit, 'A'),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cavityLevelSensorDistanceDescription'),
          setpoint: formatValueWithUnit(unitData?.spFeedCavityLevel, '%'),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.cavityLevelSensorHigh'),
          setpoint: formatValueWithUnit(unitData?.spFeedCavityLevelHighLimit, '%'),
        },
        {
          sensor: i18n.t('view.crusher.maintenance.setPoint.automation.lowCurrentRespTimeDescription'),
          setpoint: formatValueWithUnit(unitData?.spLowAmpsRespTime, 's'),
        },
      ]),
    });
  }

  return {
    key: 'automation',
    title: `${i18n.t('common.automation')}`,
    subItems: [],
    sections,
  };
};

function Setpoint({
  className,
  onChangeView,
  unitData,
  edgeStatus,
  selectedView,
  testId,
}: SetpointProps): JSX.Element | null {
  // Dependencies
  const { isSm, isMd, isXs } = useBreakpoint();
  const isMobile: boolean = isXs || isSm || isMd;

  const changeSetPoint: SetpointItemTab[] = [
    { key: SetpointType.crusher, label: `${i18n.t('common.equipmentCategory.crusher.singular')}` },
    { key: SetpointType.lubrication, label: `${i18n.t('common.lubrication')}` },
    { key: SetpointType.hydraulic, label: `${i18n.t('common.hydraulic')}` },
    { key: SetpointType.vibration, label: `${i18n.t('common.vibration')}` },
    { key: SetpointType.automation, label: `${i18n.t('common.automation')}` },
  ];

  const temperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);
  const pressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);
  const lengthUnit: LengthType = getMeasurementUnit(MeasurementType.length);
  const isDakota: boolean = isDakotaModel(unitData?.crusherModelPLC);

  const context: SetpointContext = {
    unitData,
    edgeStatus,
    temperatureUnit,
    pressureUnit,
    lengthUnit,
    isDakota,
  };

  const vibrationCardItem: MaintenanceCardItem | null = getVibrationCardItem(context);

  const cardItems: MaintenanceCardItem[] = [
    getCrusherCardItem(context),
    getLubricationCardItem(context),
    getHydraulicCardItem(context),
    ...(vibrationCardItem ? [vibrationCardItem] : []),
    getAutomationCardItem(context),
  ];

  const renderModuleByType = (type: SetpointType): JSX.Element => {
    if (type === SetpointType.crusher) {
      return <Crusher unitData={unitData} />;
    }
    if (type === SetpointType.lubrication) {
      return <Lubrication unitData={unitData} />;
    }
    if (type === SetpointType.hydraulic) {
      return <Hydraulic unitData={unitData} edgeStatus={edgeStatus} />;
    }
    if (type === SetpointType.vibration) {
      return <Vibration unitData={unitData} />;
    }
    if (type === SetpointType.automation) {
      return <Automation unitData={unitData} edgeStatus={edgeStatus} />;
    }
    return <Crusher unitData={unitData} />;
  };

  if (isMobile) {
    return (
      <Container className={className} data-testid={testId}>
        <MaintenanceCardList
          columnLabels={{
            left: i18n.t('common.sensor'),
            right: i18n.t('common.setPoint'),
          }}
          items={cardItems}
          testId={`${testId}-card-list`}
        />
      </Container>
    );
  }

  return (
    <Container className={className} data-testid={testId}>
      <FixedHeader>
        <NavigationTabs
          items={changeSetPoint}
          onClick={onChangeView}
          selected={selectedView}
        />
        <Separator />
      </FixedHeader>
      <ScrollableContent>
        {renderModuleByType(selectedView)}
      </ScrollableContent>
    </Container>
  );
}

export { Setpoint };
